import { CalendarCheck, Link2, MailCheck, ExternalLink, FileCheck2, Send } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useDossierContext } from './SuiviPrivatisationLayout';
import { PipelineFrise } from './PipelineFrise';

/** L'état d'un rendez-vous, dans les mots du destinataire : « confirmé » se lit d'un coup d'œil, pas
 *  « confirme ». Le libellé de l'attente dit QUI l'on attend — c'est l'établissement qui valide. */
const LIBELLE_RDV: Record<string, string> = {
    en_attente: 'En attente de votre confirmation',
    confirme: 'Rendez-vous confirmé',
    refuse: 'Rendez-vous refusé',
    annule: 'Rendez-vous annulé',
};
const TON_RDV: Record<string, string> = {
    en_attente: 'bg-amber-500/10 text-amber-500 border-amber-500/30',
    confirme: 'bg-emerald-500/10 text-emerald-500 border-emerald-500/30',
    refuse: 'bg-red-500/10 text-red-500 border-red-500/30',
    annule: 'bg-slate-500/10 text-slate-500 border-slate-500/30',
};

const fmtDate = (d?: string | null) => {
    if (!d) return '—';
    try { return new Date(d).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' }); } catch { return '—'; }
};
const fmtMontant = (n?: number | null) =>
    typeof n === 'number' && n > 0 ? new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(n) : '—';

/** Onglet « Pipeline » — où en est le dossier, et les quelques chiffres qui vont avec. */
export function DossierPipelinePage() {
    const { dossier } = useDossierContext();

    return (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            {/* Les dates viennent des horodatages posés par les transitions, jamais du journal : le journal
                raconte l'histoire (allers-retours compris), la frise dit l'état. */}
            <PipelineFrise stage={dossier.stage} dates={{
                plaquette_envoyee: dossier.plaquette_sent_at,
                retour_client: dossier.client_reply_at,
                brief_collecte: dossier.brief_collected_at,
                devis_envoye: dossier.devis_sent_at,
                devis_signe: dossier.devis_signed_at,
                privatisation_creee: dossier.event_created_at,
                prestataires_bookes: dossier.prestataires_at,
                fournisseurs_bookes: dossier.fournisseurs_at,
                collecte_menus: dossier.menus_started_at,
                jour_j: dossier.jour_j_at,
                cloturee: dossier.closed_at,
            }} />

            <div className="space-y-2">
                {[
                    ['Formule retenue', dossier.formule_retenue || '—'],
                    ['Montant du devis', fmtMontant(dossier.devis_amount_ttc)],
                    // La date n'existe qu'à partir de la création de la privatisation : « pas encore fixée »
                    // dit la vérité du dossier, là où un tiret laisserait croire à une donnée perdue.
                    ['Date de la privatisation', dossier.event ? fmtDate(dossier.event.start_time) : 'pas encore fixée'],
                    ['Couverts', dossier.event?.num_people ? String(dossier.event.num_people) : '—'],
                    ['Téléphone', dossier.phone || '—'],
                    ['Email', dossier.email || '—'],
                ].map(([k, v]) => (
                    <div key={k} className="flex items-baseline justify-between gap-3 py-1.5 border-b border-border last:border-0">
                        <span className="text-[11px] text-content-muted">{k}</span>
                        <span className="text-xs text-content font-semibold text-right break-words">{v}</span>
                    </div>
                ))}

                {dossier.lost_reason && (
                    <div className="p-2.5 rounded-xl bg-red-500/10 border border-red-500/30">
                        <p className="text-[10px] font-bold uppercase tracking-widest text-red-400">Motif</p>
                        <p className="text-xs text-content mt-0.5">{dossier.lost_reason}</p>
                        <p className="text-[10px] text-content-muted mt-1">Le {fmtDate(dossier.lost_at)}</p>
                    </div>
                )}

                {/* LE DEVIS SIGNÉ EST ARRIVÉ, ET PERSONNE NE L'A ENCORE OUVERT.
                    Le dépôt du client ne fait volontairement PAS avancer le dossier : « Devis signé » est le
                    jalon qui ouvre la création de la privatisation et tout le reste du parcours, il ne se
                    franchit pas sur un fichier que personne n'a regardé (décision d'Emeni, 2026-08-26).
                    D'où ce bandeau : il annonce une réception, et renvoie vers le document puis vers le
                    bouton de validation — qui vit dans l'en-tête, seul endroit d'où l'on fait avancer un
                    dossier, quel que soit l'onglet ouvert. */}
                {dossier.devis_signed_received_at && !dossier.devis_signed_at && (
                    <div className="p-2.5 rounded-xl bg-green-500/10 border border-green-500/30">
                        <p className="text-[10px] font-bold uppercase tracking-widest text-green-600 dark:text-green-400 flex items-center gap-1.5">
                            <FileCheck2 size={12} /> Devis signé reçu
                        </p>
                        <p className="text-xs text-content mt-1">
                            Le client l'a déposé le {fmtDate(dossier.devis_signed_received_at)}.
                        </p>
                        <p className="text-[11px] text-content-muted mt-1">
                            Ouvrez-le dans l'onglet <Link to={`/suivi-privatisation/${dossier.dossier_id}/documents`}
                                className="font-bold text-brand-600 dark:text-brand-400 hover:underline">Documents</Link>,
                            puis validez avec « Valider le devis signé » en haut de page — c'est ce clic qui
                            engage l'établissement.
                        </p>
                    </div>
                )}

                {/* Tant qu'on attend : dire OÙ le lien est parti évite la question « il l'a reçu, au moins ? ». */}
                {dossier.devis_sent_to && !dossier.devis_signed_received_at && !dossier.devis_signed_at && (
                    <div className="p-2.5 rounded-xl border border-border">
                        <p className="text-[10px] font-bold uppercase tracking-widest text-content-muted flex items-center gap-1.5">
                            <Send size={12} /> Lien de signature
                        </p>
                        <p className="text-[11px] text-content-muted mt-1">
                            Envoyé à <span className="font-bold text-content">{dossier.devis_sent_to}</span>
                            {dossier.devis_sent_at ? ` le ${fmtDate(dossier.devis_sent_at)}` : ''}. Le client peut y
                            déposer son exemplaire signé, ou vous dire qu'il ne donne pas suite.
                        </p>
                    </div>
                )}

                {/* LE SIGNAL QUI MANQUAIT. Emeni, 2026-08-26 : « côté suivi de privatisation, je n'ai pas
                    l'information que le client a répondu ». Le dossier restait figé sur « Plaquette envoyée »
                    alors que le client avait déjà tout rempli — personne n'aurait relancé. */}
                {dossier.plaquette_response && (
                    <div className={`p-2.5 rounded-xl border ${dossier.plaquette_response.responded ? 'bg-green-500/10 border-green-500/30' : 'border-border'}`}>
                        <p className={`text-[10px] font-bold uppercase tracking-widest flex items-center gap-1.5 ${dossier.plaquette_response.responded ? 'text-green-600 dark:text-green-400' : 'text-content-muted'}`}>
                            <MailCheck size={12} /> Réponse à la plaquette
                        </p>
                        {dossier.plaquette_response.responded ? (
                            <>
                                <p className="text-xs text-content mt-1">
                                    Le client a répondu{dossier.plaquette_response.responded_at ? ` le ${fmtDate(dossier.plaquette_response.responded_at)}` : ''}.
                                </p>
                                {dossier.plaquette_response.appointment && (
                                    <Link to="/rdv-pro" className="inline-flex items-center gap-1.5 mt-1 text-[11px] font-bold text-brand-600 dark:text-brand-400 hover:underline">
                                        Voir le rendez-vous et ses réponses <ExternalLink size={11} />
                                    </Link>
                                )}
                            </>
                        ) : (
                            <p className="text-[11px] text-content-muted mt-1">
                                En attente — le client n'a pas encore ouvert
                                {dossier.plaquette_response.template_name ? ` « ${dossier.plaquette_response.template_name} »` : ' le questionnaire'}.
                            </p>
                        )}
                    </div>
                )}

                {/* Le rendez-vous appartient à RDV Pro : on montre ce qu'il en dit, on ne le recopie pas ici,
                    sinon les deux divergeraient au premier report. */}
                <div className="p-2.5 rounded-xl border border-border">
                    <p className="text-[10px] font-bold uppercase tracking-widest text-content-muted flex items-center gap-1.5">
                        <CalendarCheck size={12} /> Rendez-vous
                    </p>
                    {dossier.rendez_vous ? (
                        <>
                            <p className="text-xs text-content mt-1">
                                {dossier.rendez_vous.subject || 'Rendez-vous'} — {fmtDate(dossier.rendez_vous.appointment_date)} à {dossier.rendez_vous.start_time}
                            </p>
                            {/* L'état du rendez-vous manquait : le suivi affichait la date sans jamais dire si
                                l'établissement l'avait confirmé, alors que le serveur renvoyait déjà ce statut.
                                Un rendez-vous en attente et un rendez-vous confirmé se lisaient pareil, et rien
                                ne signalait un refus ou une annulation. Signalé par Emeni (2026-08-30). */}
                            <p className="mt-1.5">
                                <span className={`inline-block text-[10px] font-bold px-2 py-0.5 rounded-lg border ${TON_RDV[dossier.rendez_vous.status] || TON_RDV.en_attente}`}>
                                    {LIBELLE_RDV[dossier.rendez_vous.status] || dossier.rendez_vous.status}
                                </span>
                            </p>
                        </>
                    ) : (
                        <p className="text-[11px] text-content-muted mt-1 flex items-center gap-1.5">
                            <Link2 size={11} /> Aucun rendez-vous rattaché — à prendre depuis RDV Pro.
                        </p>
                    )}
                </div>
            </div>
        </div>
    );
}
